<template>
  <div class="news-detail-page">
    <site-header />

    <section class="news-detail-page__hero">
      <div class="core-container">
        <h1 class="news-detail-page__hero-title">{{ pageTitle }}</h1>
      </div>
    </section>

    <main class="news-detail-page__main">
      <div class="core-container">
        <section class="news-detail-page__card">
          <div class="news-detail-page__inner">
            <p v-if="loading" class="news-detail-page__status">加载中...</p>
            <p v-else-if="error" class="news-detail-page__status">{{ error }}</p>
            <template v-else>
              <div class="news-detail-page__meta-bar">
                <div class="news-detail-page__meta">
                  <span>发布时间：{{ formatDate(article.published_at || article.created_at) }}</span>
                  <span class="news-detail-page__meta-divider">|</span>
                  <span>来源：{{ article.source || defaultSource }}</span>
                  <span class="news-detail-page__meta-divider">|</span>
                  <span>浏览量：{{ article.view_count || 0 }}</span>
                </div>

                <button
                  type="button"
                  class="copy-link-btn"
                  :class="{ 'copy-link-btn--copied': linkCopied }"
                  @click="copyArticleLink"
                >
                  <svg
                    v-if="!linkCopied"
                    class="copy-link-btn__icon"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <svg
                    v-else
                    class="copy-link-btn__icon"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      d="M20 6 9 17l-5-5"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span class="copy-link-btn__text">{{ linkCopied ? copySuccessText : '分享' }}</span>
                </button>
              </div>

              <div
                v-if="article.content"
                class="news-detail-page__content"
                v-html="article.content"
              />
              <p v-else class="news-detail-page__status">暂无正文</p>

              <div v-if="article.prev || article.next" class="news-detail-page__footer">
                <div class="news-detail-page__nav">
                  <div v-if="article.prev" class="nav-item nav-item--prev">
                    <router-link
                      :to="'/news/' + article.prev.id"
                      class="nav-link"
                    >
                      <span class="nav-link__label">上一篇</span>
                      <span class="nav-link__title">{{ article.prev.title }}</span>
                    </router-link>
                  </div>

                  <div v-if="article.next" class="nav-item nav-item--next">
                    <router-link
                      :to="'/news/' + article.next.id"
                      class="nav-link"
                    >
                      <span class="nav-link__label">下一篇</span>
                      <span class="nav-link__title">{{ article.next.title }}</span>
                    </router-link>
                  </div>
                </div>
              </div>

              <div class="news-detail-page__back">
                <router-link to="/news" class="back-link">← 返回资讯列表</router-link>
              </div>
            </template>
          </div>
        </section>
      </div>
    </main>

    <site-footer />
  </div>
</template>

<script>
import SiteHeader from '@/components/home/SiteHeader.vue'
import SiteFooter from '@/components/home/SiteFooter.vue'
import { fetchSiteArticle } from '@/api/site'
import { buildRichEditorFontCss } from '@/constants/richEditor'

const DEFAULT_SOURCE = '懂标帝'
const COPY_SUCCESS_TEXT = '分享链接已复制'
const COPY_TIP_DURATION_MS = 2000

export default {
  name: 'NewsDetailPage',
  components: {
    SiteHeader,
    SiteFooter
  },
  data() {
    return {
      loading: true,
      error: null,
      linkCopied: false,
      copyTipTimer: null,
      copySuccessText: COPY_SUCCESS_TEXT,
      defaultSource: DEFAULT_SOURCE,
      article: {
        title: '',
        content: '',
        source: DEFAULT_SOURCE,
        view_count: 0,
        published_at: '',
        prev: null,
        next: null
      }
    }
  },
  computed: {
    pageTitle() {
      if (this.loading) return '加载中...'
      return this.article.title || '商标资讯'
    }
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler() {
        this.loadArticle()
      }
    }
  },
  beforeDestroy() {
    this.clearCopyTipTimer()
  },
  mounted() {
    this.injectContentStyles()
  },
  methods: {
    injectContentStyles() {
      const styleId = 'news-detail-rich-content-style'
      if (document.getElementById(styleId)) return

      const style = document.createElement('style')
      style.id = styleId
      style.textContent = buildRichEditorFontCss('.news-detail-page__content')
      document.head.appendChild(style)
    },

    formatDate(value) {
      if (!value) return '-'
      const date = new Date(String(value).replace(/-/g, '/'))
      if (Number.isNaN(date.getTime())) return value
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}年${m}月${d}日`
    },

    clearCopyTipTimer() {
      if (this.copyTipTimer) {
        clearTimeout(this.copyTipTimer)
        this.copyTipTimer = null
      }
    },

    showCopySuccess() {
      this.linkCopied = true
      this.clearCopyTipTimer()
      this.copyTipTimer = setTimeout(() => {
        this.linkCopied = false
        this.copyTipTimer = null
      }, COPY_TIP_DURATION_MS)
    },

    copyArticleLink() {
      const url = window.location.href

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url)
          .then(() => {
            this.showCopySuccess()
          })
          .catch(() => {
            this.fallbackCopyLink(url)
          })
        return
      }

      this.fallbackCopyLink(url)
    },

    fallbackCopyLink(url) {
      const input = document.createElement('textarea')
      input.value = url
      input.setAttribute('readonly', '')
      input.style.position = 'absolute'
      input.style.left = '-9999px'
      document.body.appendChild(input)
      input.select()

      try {
        if (document.execCommand('copy')) {
          this.showCopySuccess()
        }
      } catch (err) {
        // ignore
      }

      document.body.removeChild(input)
    },

    loadArticle() {
      const id = parseInt(this.$route.params.id, 10)
      if (!id) {
        this.loading = false
        this.error = '资讯不存在'
        return
      }

      this.loading = true
      this.error = null
      this.linkCopied = false

      fetchSiteArticle(id)
        .then(result => {
          if (result.status !== 1 || !result.data) {
            this.error = result.desc || result.message || '资讯不存在'
            return
          }
          this.article = {
            ...this.article,
            ...result.data,
            prev: result.data.prev || null,
            next: result.data.next || null
          }
        })
        .catch(() => {
          this.error = '加载失败，请稍后重试'
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.news-detail-page {
  min-height: 100vh;
  background: var(--color-bg-main);
}

.news-detail-page__hero {
  background: linear-gradient(
    90deg,
    var(--color-banner-fill-left) 0%,
    var(--color-banner-fill-left) 42%,
    var(--color-banner-fill-right) 58%,
    var(--color-banner-fill-right) 100%
  );
  padding: var(--space-2xl) 0;
}

.news-detail-page__hero-title {
  margin: 0;
  text-align: center;
  font-size: var(--font-size-3xl);
  font-weight: 600;
  color: var(--color-text-inverse);
  line-height: 1.4;
}

.news-detail-page__main {
  padding: var(--space-lg) 0 var(--space-xl);
}

.news-detail-page__card {
  background: var(--color-bg-page);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-panel);
  padding: var(--space-xl);
  min-height: 320px;
}

.news-detail-page__inner {
  max-width: var(--site-page-content-width);
  margin: 0 auto;
}

.news-detail-page__meta-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-lg);
  margin-bottom: var(--space-lg);
}

.news-detail-page__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-sm);
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
}

.news-detail-page__meta-divider {
  color: var(--color-border);
}

.news-detail-page__content {
  font-size: var(--font-size-md);
  line-height: 1.9;
  color: var(--color-text-primary);
  word-break: break-word;
}

.news-detail-page__content :deep(p) {
  margin-bottom: var(--space-md);
}

.news-detail-page__content :deep(h2) {
  margin: var(--space-lg) 0 var(--space-md);
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--color-primary);
}

.news-detail-page__content :deep(img) {
  width: 100%;
  max-width: 100%;
  height: auto;
  display: block;
  margin: var(--space-md) auto;
  border-radius: var(--radius-sm);
}

.news-detail-page__content :deep(.ql-align-center) {
  text-align: center;
}

.news-detail-page__content :deep(.ql-align-right) {
  text-align: right;
}

.news-detail-page__status {
  text-align: center;
  padding: var(--space-2xl) 0;
  color: var(--color-text-muted);
}

.news-detail-page__footer {
  margin-top: var(--space-xl);
  padding-top: var(--space-lg);
  border-top: 1px solid var(--color-border-light);
}

.news-detail-page__nav {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-xl);
}

.nav-item {
  flex: 1;
  min-width: 0;
}

.nav-item--next {
  margin-left: auto;
  text-align: right;
}

.nav-link {
  display: inline-flex;
  flex-direction: column;
  gap: var(--space-xs);
  max-width: 100%;
  color: var(--color-text-primary);
  text-decoration: none;
  transition: color 0.2s ease;
}

.nav-link:hover {
  color: var(--color-primary);
}

.nav-link__label {
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
  line-height: 1.4;
}

.nav-link__title {
  font-size: var(--font-size-md);
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav-link--disabled {
  cursor: default;
}

.nav-link--disabled .nav-link__title {
  color: var(--color-text-muted);
}

.copy-link-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  flex-shrink: 0;
  padding: var(--space-sm) var(--space-md);
  border: none;
  border-radius: var(--radius-pill);
  background: var(--color-bg-section);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.copy-link-btn:hover {
  background: var(--color-primary-light);
  color: var(--color-primary);
}

.copy-link-btn--copied {
  background: var(--color-primary-light);
  color: var(--color-primary);
}

.copy-link-btn__icon {
  width: var(--news-detail-action-icon-size);
  height: var(--news-detail-action-icon-size);
}

.copy-link-btn__text {
  white-space: nowrap;
}

@media (max-width: 768px) {
  .news-detail-page__meta-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .copy-link-btn {
    align-self: flex-end;
  }

  .news-detail-page__nav {
    flex-direction: column;
    gap: var(--space-md);
  }

  .nav-item--next {
    text-align: left;
  }

  .nav-link__title {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}

.news-detail-page__back {
  margin-top: var(--space-xl);
  padding-top: var(--space-lg);
  border-top: 1px solid var(--color-border-light);
}

.back-link {
  color: var(--color-primary);
  font-size: var(--font-size-md);
}
</style>
